<template>
  <footer class="pt-20 px-8 sm:px-16 lg:px-20 mt-28">
    <div class="block mx-auto max-w-2k">
      <!-- Nuinalp Logo -->
      <img src="https://assets.nuinalp.com/logo/logo-typo.svg" alt="Nuinalp logo" class="logo-typo mb-8">
  
      <!-- Copyright and Social networks -->
      <div
        class="flex justify-between items-center flex-col lg:flex-row lg:py-8 pb-0 copyright-area"
      >
        <div
          class="order-2 lg:order-1 my-6 w-full lg:w-auto"
        >
          <strong
            class="copyright-message nv-d-block"
          >
            {{ $t('copyright', { currentYear: (new Date()).getFullYear() }) }}
          </strong>
          <span
            v-if="$config.public.REGISTRED_TRADEMARK"
            class="nv-my-1 nv-d-block"
          >
            {{ $t('trademark_alert') }}
          </span>
        </div>
        <!-- Util links and social networks -->
        <div class="my-2 order-1 flex flex-col lg:flex-row items-center justify-end gap-6 w-full">
          <ul class="flex-col lg:flex-row w-full lg:w-auto gap-3 hidden lg:flex">
            <li
              class="flex gap-4"
              v-for="(utilLink, idx) in utils"
              :key="idx"
            >
              <nuxt-link v-if="utilLink.url" :to="localePath(utilLink.url)">
                {{ utilLink.title }}
              </nuxt-link>
            </li>
          </ul>
  
          <div class="lg:w-auto w-full my-5">
            <span class="block my-2 text-lg lg:hidden">
              {{ $t('SOCIAL') }}
            </span>
            <!-- Social links -->
            <ul class="social-links flex items-center gap-4">
              <li
                v-for="(link, idx) in socialLinks"
                :key="idx"
              >
                <a
                  :href="link.url"
                  :title="link.title"
                  target="_blank"
                >
                  <img
                    :src="link.image"
                    :alt="link.alt"
                  >
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts" setup>
// Utils
import routesBuild from '@/utils/routes.build';
import socialLinksBuild from '@/utils/social.links.build';
import navigationLinksBuild from '@/utils/navigation.links.build';


// -----------------------------------------------
// Hooks
// -----------------------------------------------
const { $currentCallbackUrl, $config } = useNuxtApp();
const { t, locale } = useI18n();
const localePath = useLocalePath();

const utils = computed(() =>  [
  { title: t('refound_policy'), url: `${$config.public.NNP_WWW_URL}/${locale.value}/legal/refound-policy`, id: '' },
  { title: t('privacy_policy'), url: `${$config.public.NNP_WWW_URL}/${locale.value}/legal/privacy-policy`, id: '' },
  { title: t('terms_of_use'), url: `${$config.public.NNP_WWW_URL}/${locale.value}/legal/terms-of-use`, id: '' },
]);

const socialLinks = computed(() => socialLinksBuild(locale.value, [
  {
    title: t('facebook_link'), alias: 'fb', useItl: true, image: 'f-logo-color.png', alt: 'Facebook',
  },
  {
    title: t('instagram_link'), alias: 'ig', useItl: true, image: 'ig-logo-color.png', alt: 'Instagram',
  },
  {
    title: t('x_link'), alias: 'tw', useItl: true, image: 'x-logo.svg', alt: 'X',
  },
  {
    title: t('linkedin_link'), alias: 'liin', useItl: true, image: 'LI-In-bug-logo-color.png', alt: 'LinkedIn',
  },
]));

const navigationSection = computed(() => navigationLinksBuild(locale.value, [
  {
    title: t('customer'),
    items: [
      { title: t('nvpn_product_title'), path: '/home/privacy/vpn', enabled: true },
      { title: t('DIGITAL_IDENTITY_PROTECTION'), path: '/home/security/identity-protection', enabled: false },
    ],
    enabled: true,
  },
  {
    title: t('business'),
    items: [
      { title: t('POSTAL_CODES'), path: '/business/address-location/postal-codes' },
    ],
    enabled: false,
  },
  {
    title: t('FREE_TOOLS_DOWNLOADS'),
    items: [
      { title: t('NNP_PASSWORD_GENERATOR'), path: '/home/tools/web/password_generator', enabled: false },
      { title: t('NNP_QR_SCANNER'), path: '/home/tools/mobile/qr_scanner', enabled: true },
    ],
    enabled: true,
  },
  {
    title: t('business'),
    items: [
      { title: t('POSTAL_CODES'), path: '/business/address-location/postal-codes' },
    ],
    enabled: false,
  },
  {
    title: t('nuinalp_account'),
    items: [
      {
        title: t('sign_in'),
        externalPath: `https://accounts.nuinalp.com/sign-in?lang=${locale.value}&callback=${$currentCallbackUrl()}`,
      },
      {
        title: t('sign_up'),
        externalPath: `https://accounts.nuinalp.com/sign-up?lang=${locale.value}&callback=${$currentCallbackUrl()}`,
      },
      {
        title: t('about'),
        path: '/account/about',
      },
    ],
    enabled: false,
  },
  {
    title: t('developer'),
    items: [
      { title: t('acai_fw'), path: '/security' },
      { title: t('nuiverse'), path: '/security' },
      { title: t('developer_center'), path: '/security' },
    ],
    enabled: false,
  },
  {
    title: t('company'),
    items: [
      { title: t('about'), path: '/about/company-info' },
      { title: t('contact_us'), path: '/security' },
      { title: t('news'), path: '/security', enabled: false },
      { title: t('blog'), path: '/security', enabled: false },
      { title: t('report_abuse'), path: '/security', enabled: false },
      { title: t('status_service'), path: '/security', enabled: false },
    ],
  },
  {
    title: t('support'),
    items: [
      { title: t('download_and_install'), path: '/security' },
      { title: t('helper_center'), path: '/security' },
      { title: t('comunity_forum'), path: '/security' },
      { title: t('corporative_support'), path: '/security' },
      { title: t('original_software'), path: '/security' },
    ],
    enabled: false,
  },
  {
    title: t('partners'),
    items: [
      { title: t('become_partner'), path: '/security' },
      { title: t('find_partner'), path: '/security' },
      { title: t('partner_policy'), path: '/security' },
    ],
    enabled: false,
  },
]));

</script>

<style lang="scss" scoped>
footer {
  background-color: var(--nv-theme);
}

/*
-----------------------------------
* Logo typo
--------------------------------------
*/

.logo-typo {
  width: 160px;
}

/*
-----------------------------------
* Navigation sections
--------------------------------------
*/
.nav-sec-item {
  width: 20%;

  ul {
    list-style: none;

    li {
      padding: 4px 0;
    }
  }
}

.mobile-navigation-links {
  list-style: none;
}

/*
-----------------------------------
* Social networks
--------------------------------------
*/

.social-links {
  display: flex;
  list-style: none;

  li {
    align-items: center;
    display: flex;
    height: 30px;
    justify-content: center;
    width: 30px;

    a {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: 0;
    }

    img {
      width: 30px;
      max-width: unset;
    }
  }
}

.copyright-area {
  border-top: 1px solid var(--nv-theme-alt-2);
}

.copyright-message {
  font-family: 'FiraGO Italic', sans-serif;
}

.copyright-trademark-message {
  max-width: 620px;
  width: 50%;
}

/*
-----------------------------------------
Responsive
-----------------------------------------
*/

nv-collapse {
  width: 100%;
}

</style>
